import { useState } from "react";

import { AssistantClientEvent } from "@/api/model/Conversation";
import { useSendNoriMessage } from "@/api/mutations/conversation";
import { Button, LoadingSpinner, Text, YStack } from "@/components";
import Image from "@/components/Image";
import { AD_CONVERSION_EVENTS } from "@/config/adConversionEvents";
import { useAnalytics, useEventContext } from "@/hooks/analytics";
import { useCurrentConversationId } from "@/hooks/conversation";
import { useImageGalleryStore } from "@/store";

type DocumentPreviewProps = Omit<AssistantClientEvent, "content"> & {
  content: { pdf_url: string; preview_image_urls: string[] };
  isLatest: boolean;
};

const DocumentPreview = ({ content, isLatest }: DocumentPreviewProps) => {
  const { openImageGallery } = useImageGalleryStore();
  const conversationId = useCurrentConversationId();
  const { sendNoriMessage } = useSendNoriMessage(conversationId);
  const { track } = useAnalytics();
  const [submitted, setSubmitted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const eventContext = useEventContext();

  const disabled = !isLatest || submitted;
  const previewImageUrls = content.preview_image_urls;
  const firstPageUrl = previewImageUrls[0];

  const handleLooksGood = () => {
    track(AD_CONVERSION_EVENTS.outgoingDocumentSent, {
      event_context: {
        ...eventContext,
        conversationType: "nori",
      },
    });
    setSubmitted(true);
    sendNoriMessage("Looks good, please send the document.");
  };

  return (
    <YStack gap="$200">
      <Text>
        Here's a preview of what your document will look like. Let me know if
        you'd like to make any changes before sending.
      </Text>
      <Button.Wrapper
        aria-label="Open document preview"
        onPress={() => openImageGallery(previewImageUrls, firstPageUrl)}
        alignSelf="flex-start"
      >
        <Image
          source={{ uri: firstPageUrl }}
          height={180}
          aspectRatio={1}
          borderRadius="$150"
          onLoadEnd={() => setLoaded(true)}
          objectFit="contain"
        />
        {!loaded && (
          <YStack
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            alignItems="center"
            justifyContent="center"
          >
            <LoadingSpinner size="$400" />
          </YStack>
        )}
      </Button.Wrapper>
      {!disabled && <Button onPress={handleLooksGood}>Looks Good</Button>}
    </YStack>
  );
};

export default DocumentPreview;
